import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '../../lib/styles';
import { DialogLayout, DialogClose } from '../DialogLayout';
import { forwardRef, useCallback } from 'react';

export function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

const DialogOverlayBase = (
  { className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>,
  ref: React.Ref<HTMLDivElement>,
) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        'inset-0 z-400 bg-black/60',
        'fixed data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
};

const DialogOverlay = forwardRef(DialogOverlayBase);

export function Dialog({
  closeOnInteractOutside = true,
  className,
  children,
  trigger,
  overlayClassName,
  layoutClassName,
  ...rest
}: React.ComponentProps<typeof DialogPrimitive.Root> & {
  closeOnInteractOutside?: boolean;
  trigger?: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  layoutClassName?: string;
}) {
  const handleInteractOutside = useCallback(
    (event: Event) => {
      if (closeOnInteractOutside) return;

      event.preventDefault();
    },
    [closeOnInteractOutside],
  );

  const handleContentClick = useCallback((event: React.MouseEvent) => {
    // Stop propagation to prevent clicks inside the dialog from bubbling up to parent components
    event.stopPropagation();
  }, []);

  const handleRightClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <DialogPrimitive.Root data-slot="dialog" {...rest}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}

      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay className={overlayClassName} onClick={handleContentClick} onContextMenu={handleRightClick} />

        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            'fixed top-[50%] left-[50%] z-401 grid w-fit translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            className,
          )}
          onInteractOutside={handleInteractOutside}
          onContextMenu={handleRightClick}
          onClick={handleContentClick}
        >
          <DialogLayout className={cn('bg-bg-sur-primary', layoutClassName)}>{children}</DialogLayout>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

export { DialogClose };
